import React, { useMemo, useState } from "react";

// region External libraries
import { Button } from "@libraries/mui/components";
// endregion External libraries
// region Languages
import useTranslation from "@languages/useTranslation";
// endregion Languages

import HelpTip from "../HelpTip";
import { LegacyMigrationMessages } from "../languages/interfaces";
import {
  forSource, translateLockReason, translateOutcome, translateReason
} from "../messages";
import { LegacyMigrationOutcome, LegacySource } from "../service";
import * as Styled from "../styles";
import { LOCKED_REASONS, MigrationProgress } from "../useLegacyMigration";

/** The badge colour of each outcome; anything else reads as neutral. */
const OUTCOME_TONE: Record<string, "ok" | "info" | "muted" | "bad"> = {
  CREATED: "ok",
  UPDATED: "info",
  SKIPPED: "muted",
  ERROR: "bad"
};

/** Past this, a reason (usually an exception text) is cut to one line and read whole in its "?". */
const LONG_DETAIL = 70;

/** Reason codes of an account that exists but cannot be used yet. */
const PENDING_REASONS = ["RESET_EMAIL_FAILED"];

/** Something the operator has to act on: an error, an unusable account, or a lock asked for that did not happen. */
const needsAttention = (outcome: LegacyMigrationOutcome): boolean => outcome.outcome === "ERROR"
  || PENDING_REASONS.some((code) => outcome.reason?.startsWith(code))
  || (!!outcome.legacyLockReason && !LOCKED_REASONS.has(outcome.legacyLockReason));

interface IFinishedProps {
  tenantName: string;
  source: LegacySource;
  progress: MigrationProgress;
  /** What happened to each user of this run, in the order they were sent. */
  outcomes: LegacyMigrationOutcome[];
  /** Names by legacy id, for the report: the outcomes carry only the e-mail. */
  names: Map<string, string>;
  busy: boolean;
  onExport: () => void;
  onRetry: () => void;
  onLeave: () => void;
}

/**
 * The last step — what happened, in totals and user by user. A run that
 * stopped halfway, or ended with errors, can be run again: provisioning
 * matches by e-mail, so nobody is duplicated, and the next review shows who is
 * already in Identity.
 */
const Finished: React.FC<IFinishedProps> = ({
  tenantName, source, progress, outcomes, names, busy, onExport, onRetry, onLeave
}) => {

  // region Hooks
  const { t } = useTranslation();
  // endregion Hooks
  // region States
  const [onlyProblems, setOnlyProblems] = useState<boolean>(false);
  // endregion States

  const incomplete = progress.done < progress.total;
  const wrong = incomplete || progress.errors > 0;

  const problems = useMemo(() => outcomes.filter(needsAttention).length, [outcomes]);

  /** Problems first — they are what the operator came to this table for — then by name. */
  const rows = useMemo(() => outcomes
    .filter((outcome) => !onlyProblems || needsAttention(outcome))
    .map((outcome) => ({ outcome, name: names.get(outcome.legacyId) ?? "" }))
    .sort((a, b) => Number(needsAttention(b.outcome)) - Number(needsAttention(a.outcome))
      || a.name.localeCompare(b.name, undefined, { sensitivity: "base" })), [names, onlyProblems, outcomes]);

  return (
    <Styled.Card>
      <Styled.Centered>
        <Styled.Outcome $tone={wrong ? "bad" : "ok"}>{wrong ? "!" : "✓"}</Styled.Outcome>

        <Styled.StepTitle>
          {t((() => {
            if (incomplete) return LegacyMigrationMessages.legacyMigrationStoppedTitle;

            return progress.errors > 0
              ? LegacyMigrationMessages.legacyMigrationDoneWithErrorsTitle
              : LegacyMigrationMessages.legacyMigrationDoneTitle;
          })(), { tenant: tenantName })}
        </Styled.StepTitle>

        <Styled.Stats>
          <Styled.Stat $tone="ok">
            <b>{progress.created}</b>
            <span>{t(LegacyMigrationMessages.legacyMigrationStatCreated)}</span>
          </Styled.Stat>
          <Styled.Stat $tone="info">
            <b>{progress.updated}</b>
            <span>{t(LegacyMigrationMessages.legacyMigrationStatUpdated)}</span>
          </Styled.Stat>
          <Styled.Stat>
            <b>{progress.skipped}</b>
            <span>{t(LegacyMigrationMessages.legacyMigrationStatSkipped)}</span>
          </Styled.Stat>
          <Styled.Stat $tone={progress.errors > 0 ? "bad" : undefined}>
            <b>{progress.errors}</b>
            <span>{t(LegacyMigrationMessages.legacyMigrationStatErrors)}</span>
          </Styled.Stat>
        </Styled.Stats>

        <Styled.StepHint>
          {t(forSource(source, LegacyMigrationMessages.legacyMigrationDoneHint), {
            kept: progress.passwordsPreserved,
            reset: progress.resetEmailsSent
          })}
          {progress.legacyLocked > 0 && (
            <>
              {" "}
              {t(forSource(source, LegacyMigrationMessages.legacyMigrationLockedSummary), { count: progress.legacyLocked })}
            </>
          )}
        </Styled.StepHint>

        {/* A lock that did not happen leaves that user able to use the old
            login: the operator has to know, and the table says why per user. */}
        {progress.legacyLockFailed > 0 && (
          <Styled.Banner $tone="warning">
            {t(forSource(source, LegacyMigrationMessages.legacyMigrationLockFailed), { count: progress.legacyLockFailed })}
          </Styled.Banner>
        )}

        {outcomes.length > 0 && (
          <Styled.ResultReport>
            <Styled.ResultReportHeader>
              <h3>{t(LegacyMigrationMessages.legacyMigrationResultTitle)}</h3>
              <Styled.Spacer />
              {problems > 0 && (
                <Styled.FilterChip type="button" $active={onlyProblems} onClick={() => setOnlyProblems((current) => !current)}>
                  {t(LegacyMigrationMessages.legacyMigrationResultOnlyProblems)}
                  <span>{problems}</span>
                </Styled.FilterChip>
              )}
            </Styled.ResultReportHeader>

            <Styled.ResultScroll>
              <Styled.Table>
                <thead>
                  <tr>
                    <th>{t(LegacyMigrationMessages.legacyMigrationColumnUser)}</th>
                    <th>{t(LegacyMigrationMessages.legacyMigrationColumnResult)}</th>
                    <th>{t(LegacyMigrationMessages.legacyMigrationColumnPassword)}</th>
                    <th>{t(forSource(source, LegacyMigrationMessages.legacyMigrationColumnLegacy))}</th>
                    <th>{t(LegacyMigrationMessages.legacyMigrationColumnDetail)}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ outcome, name }) => (
                    <tr key={outcome.legacyId}>
                      <td>
                        <Styled.UserCell>
                          <Styled.UserName>{name || outcome.email}</Styled.UserName>
                          {name && <Styled.UserEmail>{outcome.email}</Styled.UserEmail>}
                        </Styled.UserCell>
                      </td>
                      <td>
                        <Styled.Badge $tone={OUTCOME_TONE[outcome.outcome] ?? "muted"}>
                          {translateOutcome(t, outcome.outcome)}
                        </Styled.Badge>
                      </td>
                      <td>
                        {(() => {
                          if (outcome.passwordPreserved) return t(LegacyMigrationMessages.legacyMigrationResultPasswordKept);

                          return outcome.resetEmailSent ? t(LegacyMigrationMessages.legacyMigrationResultEmailSent) : "—";
                        })()}
                      </td>
                      <td>
                        {outcome.legacyLockReason ? (
                          <Styled.Badge $tone={LOCKED_REASONS.has(outcome.legacyLockReason) ? "ok" : "new"}>
                            {translateLockReason(t, outcome.legacyLockReason, source)}
                          </Styled.Badge>
                        ) : (
                          <Styled.Badge $tone="muted">{t(LegacyMigrationMessages.legacyMigrationResultLockNotAsked)}</Styled.Badge>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const detail = translateReason(t, outcome.reason) || "—";

                          return (
                            <Styled.ResultDetail>
                              <Styled.ResultDetailText>{detail}</Styled.ResultDetailText>
                              {detail.length > LONG_DETAIL && <HelpTip text={detail} />}
                            </Styled.ResultDetail>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Styled.Table>
            </Styled.ResultScroll>
          </Styled.ResultReport>
        )}

        <Styled.Actions>
          <Button disabled={busy || progress.done === 0} onClick={onExport}>
            {t(LegacyMigrationMessages.legacyMigrationExport)}
          </Button>
          {wrong && (
            <Button disabled={busy} onClick={onRetry}>
              {t(LegacyMigrationMessages.legacyMigrationRetry)}
            </Button>
          )}
          <Styled.Spacer />
          <Button variant="contained" disabled={busy} onClick={onLeave}>
            {t(LegacyMigrationMessages.legacyMigrationLeave)}
          </Button>
        </Styled.Actions>
      </Styled.Centered>
    </Styled.Card>
  );
};

export default Finished;
